import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, ButtonBase, Checkbox, CircularProgress, Snackbar, Typography } from '@mui/material';
import { SearchFilter, TogglePill } from '../monthly/MultiSelectFilter';
import { GREEN, GREEN_SOFT, INK, LINE, MUTED } from '../home/homeTheme';
import { fetchProjectActivities, saveProjectActivities } from '../../utils/projectsService';

const sameSet = (a, b) => a.length === b.length && a.every((id) => b.includes(id));

/**
 * Scheda Activities: le activities che si possono registrare sul progetto.
 * Ricerca, "solo selezionate", seleziona/togli tutte (tra quelle visibili) e barra di salvataggio fissa.
 */
export default function ProjectActivities({ projectId, canEdit, onDirtyChange }) {
  const [activities, setActivities] = useState([]);
  const [saved, setSaved] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState(null);
  const [search, setSearch] = useState('');
  const [onlySelected, setOnlySelected] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchProjectActivities(projectId)
      .then(({ activities: list, selectedIds }) => {
        if (cancelled) return;
        setActivities([...list].sort((a, b) => String(a.name).localeCompare(String(b.name))));
        setSaved(selectedIds);
        setSelected(selectedIds);
      })
      .catch((e) => !cancelled && setError(`Unable to load activities: ${e.message}`))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const dirty = !sameSet(selected, saved);
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
  const changes = selected.filter((id) => !saved.includes(id)).length + saved.filter((id) => !selected.includes(id)).length;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return activities.filter((a) => {
      if (onlySelected && !selected.includes(a.id)) return false;
      return !q || [a.name, a.description].some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [activities, search, onlySelected, selected]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const selectVisible = (on) => {
    const ids = visible.map((a) => a.id);
    setSelected((s) => (on ? [...new Set([...s, ...ids])] : s.filter((id) => !ids.includes(id))));
  };
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveProjectActivities(projectId, selected);
      setSaved(selected);
      setSnack('Activities saved');
    } catch (e) {
      setError(`Not saved: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress color="success" />
      </Box>
    );
  }

  const allVisibleSelected = visible.length > 0 && visible.every((a) => selected.includes(a.id));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <SearchFilter value={search} onChange={setSearch} placeholder="Search activities" width={280} />
        <TogglePill label={`Selected only (${selected.length})`} checked={onlySelected} onChange={setOnlySelected} />
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 13, color: MUTED }}>
          {selected.length} of {activities.length} activities available on this project
        </Typography>
        {canEdit && (
          <Button size="small" color="inherit" onClick={() => selectVisible(!allVisibleSelected)} disabled={!visible.length} sx={{ textTransform: 'none', fontWeight: 600 }}>
            {allVisibleSelected ? 'Clear' : 'Select'} {visible.length === activities.length ? 'all' : `these ${visible.length}`}
          </Button>
        )}
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
        {visible.map((a) => {
          const on = selected.includes(a.id);
          return (
            <ButtonBase
              key={a.id}
              disabled={!canEdit}
              onClick={() => toggle(a.id)}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                gap: 0.5,
                textAlign: 'left',
                p: 1,
                pr: 1.5,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: on ? GREEN : LINE,
                bgcolor: on ? GREEN_SOFT : 'var(--rt-paper)',
                transition: 'border-color 0.15s ease, background-color 0.15s ease',
                '&:hover': { borderColor: on ? GREEN : 'var(--rt-pill-border-hover)' },
              }}
            >
              <Checkbox checked={on} color="success" size="small" tabIndex={-1} disableRipple sx={{ p: 0.5 }} />
              <Box sx={{ minWidth: 0, pt: 0.4 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: INK }}>{a.name}</Typography>
                {a.description && <Typography sx={{ fontSize: 12, color: MUTED }}>{a.description}</Typography>}
              </Box>
            </ButtonBase>
          );
        })}
        {!visible.length && <Typography sx={{ fontSize: 13.5, color: MUTED, py: 3 }}>No activities match these filters.</Typography>}
      </Box>

      {/* Barra di salvataggio: compare solo con modifiche non salvate */}
      {dirty && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 16,
            zIndex: 5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.25,
            borderRadius: 3,
            bgcolor: 'var(--rt-paper)',
            border: `1px solid ${LINE}`,
            boxShadow: '0 12px 32px -12px rgba(15,23,18,0.35)',
          }}
        >
          <Typography sx={{ fontSize: 13.5, color: INK, flex: 1 }}>
            <strong>{changes}</strong> change{changes === 1 ? '' : 's'} not saved
          </Typography>
          <Button color="inherit" onClick={() => setSelected(saved)} disabled={saving} sx={{ textTransform: 'none' }}>
            Discard
          </Button>
          <Button variant="contained" color="success" onClick={save} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none', minWidth: 110, bgcolor: GREEN }}>
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Save'}
          </Button>
        </Box>
      )}

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack(null)} message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}
